import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  FolderOpen,
  Bot,
  Database,
  GitBranch,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  Settings,
  Coins,
  DollarSign,
} from 'lucide-react';
import type { TokenUsageSummary } from '../types/tokenUsage';
import { useProjectStore, useAuthStore, useUIStore } from '../store';
import Button from './common/Button';
import Logo from './common/Logo';
import apiService from '../services/api';
import { useAssistantContext } from '../contexts/AssistantContext';
import { ASSISTANT_NAME } from '../constants/branding';
import { useAllWorkflows } from '../hooks/queries/useAllWorkflows';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  // AC-1410 / AC-1377 P4.4: `allWorkflows` is read through `useAllWorkflows`
  // (TanStack Query) instead of `useProjectStore.allWorkflows`. The store's
  // slice is intentionally left in place during transition - AC-1411 /
  // AC-1412 finishes the cleanup.
  const { projects, loadProjects, setCurrentOperation, allAgents, loadAllAgents, allModels, loadAllModels, operationModalOpen, openOperationModal, closeOperationModal, createProject, loadLLMCatalog } = useProjectStore();
  const allWorkflowsQuery = useAllWorkflows();
  const allWorkflows = allWorkflowsQuery.data ?? [];
  const { setContext } = useAssistantContext();
  const { openAssistant } = useUIStore();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
    loadAllAgents();
    loadAllModels();
    loadLLMCatalog();
    loadRecentActivities();
  }, [loadProjects, loadAllAgents, loadAllModels, loadLLMCatalog]);

  // Set AssistantContext to general when on dashboard
  useEffect(() => {
    setContext({
      contextType: 'general'
    });
  }, [setContext]);

  const loadRecentActivities = async () => {
    try {
      setActivityLoading(true);
      const response = await apiService.getRecentActivities({
        limit: 15,
        daysBack: 7
      });
      setRecentActivity(response.activities);
    } catch (error) {
      console.error('Failed to load recent activities:', error);
      // Fallback to empty array on error
      setRecentActivity([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleCreateOperation = () => {
    navigate('/projects');
  };

  const handleSelectOperation = (project: any) => {
    setCurrentOperation(project);
    navigate(`/projects/${project.project_id}`);
  };

  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showAllOperations, setShowAllOperations] = useState(false);
  const [usageSummary, setUsageSummary] = useState<TokenUsageSummary | null>(null);
  const [usageLoading, setUsageLoading] = useState(true);

  useEffect(() => {
    const loadUsage = async () => {
      try {
        setUsageLoading(true);
        const summary = await apiService.getUsageSummary({ period: 'this_month' });
        setUsageSummary(summary);
      } catch (err) {
        console.error('Failed to load usage summary:', err);
      } finally {
        setUsageLoading(false);
      }
    };
    loadUsage();
  }, []);

  const formatNumber = (n: number) => n.toLocaleString();
  const formatCurrency = (n: number) => n < 1 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-warm-white">
          {projects.length === 0
            ? `Welcome to Agent Commander, ${user?.username || 'User'}!`
            : `Welcome back, ${user?.username || 'User'}!`}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Overview of your AI workflow projects and activities
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Total Projects (clickable) with Workflows/Agents/Schemas sub-stats */}
        <div
          className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-primary-500 cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:shadow-lg transition-all ring-0 hover:ring-2 ring-primary-400"
          onClick={() => navigate('/projects')}
          tabIndex={0}
          role="button"
          aria-label="View all projects"
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/projects'); }}
        >
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-orange-100 dark:bg-orange-900/30">
              <FolderOpen className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Projects</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-warm-white">{projects.length}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 space-y-1">
            <div className="flex justify-between">
              <span>Workflows:</span>
              <span>{allWorkflows.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Agents:</span>
              <span>{allAgents.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Schemas:</span>
              <span>{allModels.length}</span>
            </div>
          </div>
        </div>

        {/* Total Tokens (clickable) */}
        {usageLoading ? (
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-blue-500">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-3" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            </div>
          </div>
        ) : (
          <div
            className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-blue-500 cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:shadow-lg transition-all ring-0 hover:ring-2 ring-primary-400"
            onClick={() => navigate('/settings?tab=usage')}
            tabIndex={0}
            role="button"
            aria-label="View token usage details"
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate('/settings?tab=usage'); }}
          >
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Coins className="w-6 h-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tokens <span className="text-xs">(This Month)</span></p>
                <p className="text-2xl font-bold text-gray-900 dark:text-warm-white">
                  {usageSummary ? formatNumber(usageSummary.total_tokens) : '--'}
                </p>
              </div>
            </div>
            {usageSummary && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <div className="flex justify-between">
                  <span>Prompt:</span>
                  <span>{formatNumber(usageSummary.prompt_tokens)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Completion:</span>
                  <span>{formatNumber(usageSummary.completion_tokens)}</span>
                </div>
                <div className="flex justify-between">
                  <span>API Calls:</span>
                  <span>{formatNumber(usageSummary.api_call_count)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Total Cost */}
        {usageLoading ? (
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-green-500">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-3" />
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-green-500">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cost <span className="text-xs">(This Month)</span></p>
                <p className="text-2xl font-bold text-gray-900 dark:text-warm-white">
                  {usageSummary ? formatCurrency(usageSummary.total_cost) : '--'}
                </p>
              </div>
            </div>
            {usageSummary?.budget && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <div className="flex justify-between">
                  <span>Budget:</span>
                  <span>{formatCurrency(usageSummary.budget.monthly_limit)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Remaining:</span>
                  <span>{formatCurrency(usageSummary.budget.monthly_limit - usageSummary.budget.current_spend)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Used:</span>
                  <span className={
                    usageSummary.budget.percentage_used >= 90 ? 'text-red-500' :
                    usageSummary.budget.percentage_used >= 75 ? 'text-yellow-500' :
                    'text-green-500'
                  }>
                    {usageSummary.budget.percentage_used.toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        {/* Recent Projects */}
        <div className="bg-white dark:bg-cap-dark rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Recent Projects</h2>
              <Button onClick={openOperationModal}>
                <Plus className="w-4 h-4 mr-1" />
                New Project
              </Button>
            </div>
          </div>
          <div className="p-6">
            {projects.length === 0 ? (
              <div className="text-center py-8">
                <FolderOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">No projects yet</p>
                <Button onClick={openOperationModal}>
                  Create Your First Project
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className={`space-y-3 ${showAllOperations && projects.length > 8 ? 'max-h-96 overflow-y-auto' : ''}`}>
                  {projects
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                    .slice(0, showAllOperations ? projects.length : 5)
                    .map((project) => (
                    <div
                      key={project.project_id}
                      onClick={() => handleSelectOperation(project)}
                      className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:shadow-md cursor-pointer transition-all"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <FolderOpen className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-warm-white">{project.name}</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                            {project.description}
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-gray-400 hidden sm:block">
                        {new Date(project.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Show More/Less Button */}
                {projects.length > 5 && (
                  <div className="pt-4 border-t border-gray-100">
                    <button
                      onClick={() => setShowAllOperations(!showAllOperations)}
                      className="w-full text-sm text-primary-600 hover:text-primary-800 hover:underline py-2"
                    >
                      {showAllOperations ? 'Show Less' : `Show ${projects.length - 5} More`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-cap-dark rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Recent Activity</h2>
            <button
              onClick={() => navigate('/activities')}
              className="text-sm text-primary-600 hover:text-primary-800 hover:underline"
            >
              View All
            </button>
          </div>
          <div className="p-6">
            <div className={`space-y-4 ${showAllActivities && recentActivity.length > 8 ? 'max-h-96 overflow-y-auto' : ''}`}>
              {activityLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading activities...</p>
                </div>
              ) : recentActivity.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No recent activity</p>
                </div>
              ) : (
                recentActivity
                  .slice(0, showAllActivities ? recentActivity.length : 5)
                  .map((activity) => {
                  // AC-1422: orchestration outcomes use shape + color (CheckCircle
                  // for success, XCircle for failure) so they are distinguishable
                  // in grayscale and to color-blind users. Other entity types keep
                  // their entity icon but still get a red override on error.
                  const activityType = activity.activity_type;
                  const isOrchOutcome =
                    activityType === 'orchestration_run' ||
                    activityType === 'orchestration_failed';
                  const isFailure =
                    activity.status === 'error' || activityType === 'orchestration_failed';

                  const getIcon = () => {
                    let entityIcon: React.ReactElement;
                    let iconColor = 'text-primary-600';

                    if (isOrchOutcome) {
                      entityIcon = isFailure
                        ? <XCircle className="w-5 h-5" />
                        : <CheckCircle className="w-5 h-5" />;
                      iconColor = isFailure
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-emerald-600 dark:text-emerald-400';
                    } else if (activityType.startsWith('project_')) {
                      entityIcon = <FolderOpen className="w-5 h-5" />;
                      iconColor = 'text-primary-600';
                    } else if (activityType.startsWith('agent_')) {
                      entityIcon = <Bot className="w-5 h-5" />;
                      iconColor = 'text-emerald-600 dark:text-emerald-400';
                    } else if (activityType.startsWith('model_')) {
                      entityIcon = <Database className="w-5 h-5" />;
                      iconColor = 'text-purple-600';
                    } else if (activityType.startsWith('orchestration_')) {
                      entityIcon = <GitBranch className="w-5 h-5" />;
                      iconColor = 'text-primary-600';
                    } else {
                      entityIcon = <Activity className="w-5 h-5" />;
                      iconColor = 'text-gray-600 dark:text-gray-400';
                    }

                    if (activity.status === 'error' && !isOrchOutcome) {
                      iconColor = 'text-red-600 dark:text-red-400';
                    } else if (activity.status === 'warning') {
                      iconColor = 'text-yellow-600 dark:text-yellow-400';
                    }

                    return React.cloneElement(entityIcon, { className: `w-5 h-5 ${iconColor}` });
                  };

                  const titleClass = isOrchOutcome
                    ? (isFailure
                        ? 'text-sm font-medium text-red-700 dark:text-red-400'
                        : 'text-sm font-medium text-emerald-700 dark:text-emerald-400')
                    : 'text-sm font-medium text-gray-900 dark:text-warm-white';

                  const formatTime = (timestamp: string) => {
                    const date = new Date(timestamp);
                    const now = new Date();
                    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
                    
                    if (diffInMinutes < 1) return 'Just now';
                    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
                    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
                    return `${Math.floor(diffInMinutes / 1440)} days ago`;
                  };

                  return (
                    <div key={activity.activity_log_id} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
                      <div className="flex-1">
                        <p className={titleClass}>
                          {activity.title}
                        </p>
                        {activity.activity_data?.project_name && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            in {(() => {
                              // Check if the project still exists
                              const project = projects.find(p => p.name === activity.activity_data.project_name);
                              if (project) {
                                // Project exists - show as clickable link
                                return (
                                  <button 
                                    onClick={() => handleSelectOperation(project)}
                                    className="font-medium text-primary-600 hover:text-primary-800 hover:underline cursor-pointer"
                                  >
                                    {activity.activity_data.project_name}
                                  </button>
                                );
                              } else {
                                // Project no longer exists - show as plain text
                                return (
                                  <span className="font-medium text-gray-500 dark:text-gray-400">
                                    {activity.activity_data.project_name}
                                  </span>
                                );
                              }
                            })()}
                          </p>
                        )}
                        <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatTime(activity.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              
              {/* Show More/Less Button */}
              {recentActivity.length > 5 && (
                <div className="pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setShowAllActivities(!showAllActivities)}
                    className="w-full text-sm text-primary-600 hover:text-primary-800 hover:underline py-2"
                  >
                    {showAllActivities ? 'Show Less' : `Show ${recentActivity.length - 5} More`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 sm:mt-8 bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {/* AC-1702: primary creation path — open the Agent Commander copilot.
              Spans the row on mobile so it reads as the headline action. */}
          <button
            onClick={openAssistant}
            data-testid="assistant-launcher-dashboard"
            className="col-span-2 md:col-span-1 flex items-center justify-center p-4 min-h-[80px] rounded-lg bg-primary-600 text-white hover:bg-primary-700 shadow-sm transition-colors"
          >
            <div className="text-center flex flex-col items-center">
              <Logo variant="static-transparent" size={30} className="mb-2" />
              <span className="text-sm font-semibold">Build with {ASSISTANT_NAME}</span>
            </div>
          </button>

          <button
            onClick={handleCreateOperation}
            className="flex items-center justify-center p-4 min-h-[80px] border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="text-center">
              <Plus className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Create Project</span>
            </div>
          </button>

          <button
            onClick={() => navigate('/projects')}
            className="flex items-center justify-center p-4 min-h-[80px] border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="text-center">
              <FolderOpen className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">View All Operations</span>
            </div>
          </button>
          
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center justify-center p-4 min-h-[80px] border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="text-center">
              <Settings className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Settings</span>
            </div>
          </button>
        </div>
      </div>

      {operationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl w-full max-w-[calc(100%-2rem)] sm:max-w-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white mb-4">Create New Project</h2>
            <form onSubmit={async (e) => {
              e.preventDefault();
              setLoading(true);
              setError(null);
              const result = await createProject({ name, description });
              setLoading(false);
              if (result.success) {
                closeOperationModal();
                setName('');
                setDescription('');
                setError(null);
                setCurrentOperation(result.project);
                navigate(`/projects/${result.project.project_id}`);
              } else {
                setError(result.error);
              }
            }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter project name"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Enter project description"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeOperationModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 dark:hover:bg-gray-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create Project'}
                </button>
              </div>
              {error && <div className="text-red-500 mt-2 text-sm">{error}</div>}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;